import { useCallback, useEffect, useState } from 'react';

import { INSUFFICIENT_CREDITS_EVENT, type InsufficientCreditsEventDetail } from '../lib/api';
import type { ActionType } from './useCredits';

export interface InsufficientCreditsState {
  open: boolean;
  actionType?: string;
}

/**
 * Fase 3 — Verifica se uma ação deve ser bloqueada por falta de créditos,
 * sem depender de DOM. Usado como check defensivo antes de navegar/chamar
 * o backend (ex.: `Catalogo.tsx` antes de abrir simulado).
 *
 * Retorna `true` quando o balance está carregado e o total do actionType
 * é <= 0. Enquanto `balance` é nulo (loading), retorna `false` — deixamos
 * o backend responder e o interceptador de 402 abrir o modal se preciso.
 */
export function requireCredits(
  actionType: ActionType,
  totals: Partial<Record<ActionType, number>> | null | undefined
): boolean {
  if (!totals) return false;
  const total = totals[actionType];
  if (total === undefined) return false;
  return total <= 0;
}

/**
 * Hook global que escuta o evento `insufficient-credits` (disparado por
 * `apiClient` em 402) e também expõe `openInsufficientCreditsModal` para
 * disparo manual (ex.: prevenção proativa em `Catalogo`).
 *
 * Monta apenas um listener por App — renderizado uma vez no root. O modal
 * correspondente (`NoCreditsModal`) é controlado pelo estado retornado.
 */
export function useInsufficientCreditsHandler() {
  const [state, setState] = useState<InsufficientCreditsState>({ open: false });

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<InsufficientCreditsEventDetail>;
      setState({ open: true, actionType: custom.detail?.actionType });
    };
    window.addEventListener(INSUFFICIENT_CREDITS_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(INSUFFICIENT_CREDITS_EVENT, handler as EventListener);
    };
  }, []);

  const openInsufficientCreditsModal = useCallback((actionType?: string) => {
    setState({ open: true, actionType });
  }, []);

  const closeInsufficientCreditsModal = useCallback(() => {
    setState({ open: false });
  }, []);

  return {
    state,
    openInsufficientCreditsModal,
    closeInsufficientCreditsModal,
  };
}
